import React, { useEffect, useRef, Children, createElement } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Layers, PanelTop, DoorOpen, StretchHorizontal, Sofa, Forklift, Sprout, Award, Lightbulb, Globe, Briefcase, ArrowRight, ChevronDown, Factory, Paintbrush, Ship, ClipboardList, Settings2, Tag, Truck, CheckCircle2, Users } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 60
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
const fadeIn = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 1,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function HomePage() {
  const heroRef = useRef(null);
  const {
    scrollY
  } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 1000], [0, 250]);
  const {
    t
  } = useLanguage();
  useEffect(() => {
    document.title = 'SmartImport Internacional | Materiales arquitectónicos';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Paneles decorativos, suelos SPC y puertas de alta calidad para distribuidores, arquitectos y proyectos profesionales.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Paneles decorativos, suelos SPC y puertas de alta calidad para distribuidores, arquitectos y proyectos profesionales.';
      document.head.appendChild(meta);
    }
  }, []);
  return <div className="w-full" data-id="element-280">
      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-[85vh] md:min-h-screen w-full flex items-center justify-center overflow-hidden" data-id="element-281">
        <motion.div className="absolute inset-0 z-0" style={{
        y: parallaxY
      }} data-id="element-282">
          <img src="https://cdn.magicpatterns.com/uploads/qS64CvcJQa1nCJvnKZC5xd/1_imagen_hero_.png" alt="Arquitectura moderna" className="w-full h-[120%] object-cover object-center" data-id="element-283" />
        </motion.div>
        <div className="absolute inset-0 z-10" style={{
        background: 'linear-gradient(90deg, rgba(10,20,35,0.65) 0%, rgba(10,20,35,0.45) 35%, rgba(10,20,35,0.15) 60%, rgba(10,20,35,0) 100%)'
      }} data-id="element-284"></div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-20" data-id="element-285">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl" data-id="element-286">
            <motion.h1 variants={fadeInUp} className="text-[32px] md:text-[44px] lg:text-[64px] font-heading font-bold text-white leading-[1.1] mb-8 tracking-tight" data-id="element-287">
              {t('home.hero.title')}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl lg:text-2xl text-white/80 mb-12 max-w-2xl font-light leading-relaxed" data-id="element-288">
              {t('home.hero.desc')}
            </motion.p>
            <motion.div variants={fadeInUp} data-id="element-289">
              <Link to="/catalogo" className="inline-flex items-center justify-center px-10 py-5 text-base font-medium text-white bg-corporate-blue hover:bg-blue-700 rounded-md shadow-lg hover:-translate-y-0.5 hover:shadow-[0_10px_25px_rgba(0,0,0,0.15)] transition-all duration-300 ease-out" data-id="element-290">
                {t('home.hero.cta')}
                <ArrowRight className="ml-3 h-5 w-5" data-id="element-291" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1
      }} className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center text-white/60" data-id="element-292">
          <span className="text-xs uppercase tracking-widest mb-2 font-light" data-id="element-293">
            {t('home.hero.scroll')}
          </span>
          <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2.5,
          ease: 'easeInOut'
        }} data-id="element-294">
            <ChevronDown className="h-5 w-5" data-id="element-295" />
          </motion.div>
        </motion.div>
      </section>

      {/* SOLUTIONS SECTION */}
      <section className="py-32 lg:py-40 bg-white relative" data-id="element-296">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-297">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-298">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-6 tracking-tight" data-id="element-299">
              {t('home.solutions.title')}
            </h2>
            <div className="w-16 h-0.5 bg-accent-beige mx-auto" data-id="element-300"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-301">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="relative h-[500px] lg:h-[700px] rounded-sm overflow-hidden shadow-2xl group" data-id="element-302">
              <img src="https://cdn.magicpatterns.com/uploads/eLsX5iWTc3BRaCo3rFXREb/2_imagen_de_soluciones.png" alt="Soluciones de materiales" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-303" />
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-304">
              {[{
              title: 'SmartFloor',
              desc: t('home.solutions.floor.desc'),
              icon: Layers,
              link: '/soluciones/smartfloor'
            }, {
              title: 'SmartPanel',
              desc: t('home.solutions.panel.desc'),
              icon: PanelTop,
              link: '/soluciones/smartpanel'
            }, {
              title: 'SmartPanel Exterior & Decorativo',
              desc: t('home.solutions.strips.desc'),
              icon: StretchHorizontal,
              link: '/soluciones/smartstrips'
            }, {
              title: 'SmartDoor',
              desc: t('home.solutions.door.desc'),
              icon: DoorOpen,
              link: '/soluciones/smartdoor'
            }, {
              title: 'SmartSofa',
              desc: t('home.solutions.sofa.desc'),
              icon: Sofa,
              link: '/soluciones/smartsofa'
            }, {
              title: 'SmartMachinery',
              desc: t('home.solutions.machinery.desc'),
              icon: Forklift,
              link: '/soluciones/smartmachinery'
            }, {
              title: 'SmartGarden',
              desc: t('home.solutions.garden.desc'),
              icon: Sprout,
              link: '/soluciones/smartgarden'
            }].map((item, index) => <motion.div key={index} variants={fadeInUp} data-id="element-305">
                  <Link to={item.link} className="group block p-10 bg-neutral-grey border-t-2 border-transparent hover:border-accent-beige rounded-sm hover:bg-white hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 ease-premium h-full" data-id="element-306">
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-8 shadow-sm group-hover:bg-corporate-blue transition-colors duration-500 ease-premium" data-id="element-307">
                      <item.icon className="h-7 w-7 text-corporate-blue group-hover:text-white transition-colors duration-500 ease-premium" data-id="element-308" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-3" data-id="element-309">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-8 leading-relaxed font-light" data-id="element-310">
                      {item.desc}
                    </p>
                    <div className="flex items-center text-corporate-blue font-medium text-sm group-hover:translate-x-2 transition-transform duration-500 ease-premium" data-id="element-311">
                      {t('home.solutions.details')}{' '}
                      <ArrowRight className="ml-2 h-4 w-4" data-id="element-312" />
                    </div>
                  </Link>
                </motion.div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRAND DEVELOPMENT SECTION */}
      <section className="py-32 lg:py-40 bg-neutral-grey border-t border-gray-100" data-id="element-313">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-314">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-315">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-6 tracking-tight" data-id="element-316">
              {t('home.brand.title')}
            </h2>
            <div className="w-16 h-0.5 bg-accent-beige mx-auto mb-8" data-id="element-317"></div>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto font-light leading-relaxed" data-id="element-318">
              {t('home.brand.desc')}
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-id="element-319">
            {[{
            icon: Factory,
            title: t('home.brand.b1.title'),
            desc: t('home.brand.b1.desc')
          }, {
            icon: Paintbrush,
            title: t('home.brand.b2.title'),
            desc: t('home.brand.b2.desc')
          }, {
            icon: Ship,
            title: t('home.brand.b3.title'),
            desc: t('home.brand.b3.desc')
          }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white p-10 rounded-sm border-t-2 border-transparent hover:border-accent-beige hover:shadow-xl hover:-translate-y-2 transition-all duration-500 ease-premium" data-id="element-320">
                <div className="w-14 h-14 bg-neutral-grey rounded-full flex items-center justify-center mb-8 shadow-sm" data-id="element-321">
                  <item.icon className="h-7 w-7 text-corporate-blue" data-id="element-322" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-4 tracking-tight" data-id="element-323">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-base font-light leading-relaxed" data-id="element-324">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CÓMO TRABAJAMOS SECTION */}
      <section className="py-32 lg:py-40 bg-white border-t border-gray-100" data-id="element-325">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-326">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-327">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-6 tracking-tight" data-id="element-328">
              {t('home.how.title')}
            </h2>
            <div className="w-16 h-0.5 bg-accent-beige mx-auto mb-8" data-id="element-329"></div>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto font-light leading-relaxed" data-id="element-330">
              {t('home.how.desc')}
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8" data-id="element-331">
            {[{
            step: '01',
            title: t('home.how.s1.title'),
            desc: t('home.how.s1.desc'),
            image: 'https://cdn.magicpatterns.com/uploads/aJqxsSCine8yHCANqSBYLQ/imagen_1.png'
          }, {
            step: '02',
            title: t('home.how.s2.title'),
            desc: t('home.how.s2.desc'),
            image: 'https://cdn.magicpatterns.com/uploads/ahW8BJ2ctGjTc3wHi8ZZ18/imagen_2_.png'
          }, {
            step: '03',
            title: t('home.how.s3.title'),
            desc: t('home.how.s3.desc'),
            image: 'https://cdn.magicpatterns.com/uploads/jFgfn1SpZez9xssivpB9C9/imagen_3.png'
          }, {
            step: '04',
            title: t('home.how.s4.title'),
            desc: t('home.how.s4.desc'),
            image: 'https://cdn.magicpatterns.com/uploads/aT3buRKD3umSYqAJa7Ee1n/imagen_4.png'
          }, {
            step: '05',
            title: t('home.how.s5.title'),
            desc: t('home.how.s5.desc'),
            image: 'https://cdn.magicpatterns.com/uploads/rg9LfD3PkSRbv8ynz4KvHS/imagen_5.png'
          }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-neutral-grey rounded-sm overflow-hidden group border border-gray-100 hover:-translate-y-2 hover:shadow-xl transition-all duration-500 ease-premium" data-id="element-332">
                <div className="h-48 relative overflow-hidden" data-id="element-333">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-110" data-id="element-334" />
                </div>
                <div className="p-6" data-id="element-335">
                  <div className="text-xs font-heading font-bold text-corporate-blue tracking-widest mb-3" data-id="element-336">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-heading font-bold text-corporate-dark mb-2 tracking-tight" data-id="element-337">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm font-light leading-relaxed" data-id="element-338">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* APPLICATIONS SECTION */}
      <section className="py-32 lg:py-40 bg-corporate-dark text-white overflow-hidden" data-id="element-339">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-340">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-341">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} className="order-2 lg:order-1" data-id="element-342">
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-heading font-bold mb-8 tracking-tight" data-id="element-343">
                {t('home.apps.title')}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-300 text-xl mb-12 font-light leading-relaxed" data-id="element-344">
                {t('home.apps.desc')}
              </motion.p>

              <div className="grid grid-cols-2 gap-6" data-id="element-345">
                {[t('home.apps.hotels'), t('home.apps.offices'), t('home.apps.residential'), t('home.apps.commercial')].map((app, idx) => <motion.div key={idx} variants={fadeInUp} className="border border-white/20 p-8 rounded-sm hover:bg-white/5 hover:border-accent-beige/50 transition-all duration-500 ease-premium" data-id="element-346">
                    <h4 className="font-heading font-medium text-xl text-accent-light" data-id="element-347">
                      {app}
                    </h4>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="order-1 lg:order-2 relative h-[600px] rounded-sm overflow-hidden group" data-id="element-348">
              <img src="https://cdn.magicpatterns.com/uploads/cRNBeqAVKM8j3ixv9SgDRg/3️⃣_Imagen_de_aplicaciones__proyectos_copia.png" alt="Aplicaciones en proyectos" className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-349" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SOLUCIONES PARA DISTRIBUIDORES */}
      <section className="py-32 lg:py-40 bg-white border-t border-gray-100" data-id="element-350">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-352">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-353">
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-354">
                {t('home.dist.title')}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-600 text-lg mb-10 font-light leading-relaxed" data-id="element-355">
                {t('home.dist.desc')}
              </motion.p>
              <motion.ul variants={staggerContainer} className="space-y-5" data-id="element-356">
                {[t('home.dist.p1'), t('home.dist.p2'), t('home.dist.p3')].map((point, idx) => <motion.li key={idx} variants={fadeInUp} className="flex items-start" data-id="element-357">
                      <CheckCircle2 className="h-6 w-6 text-corporate-blue mr-4 flex-shrink-0 mt-0.5" data-id="element-358" />
                      <span className="text-gray-700 text-lg font-light" data-id="element-359">
                        {point}
                      </span>
                    </motion.li>)}
              </motion.ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="bg-neutral-grey rounded-sm overflow-hidden" data-id="element-360">
              <div className="h-64 lg:h-80 overflow-hidden" data-id="element-361">
                <img src="https://cdn.magicpatterns.com/uploads/6HPGK2LbLH8THmDGV3gTjq/imagen_6.png" alt="Reunión profesional revisando muestras de materiales arquitectónicos" className="w-full h-full object-cover" data-id="element-362" />
              </div>
              <div className="p-10 text-center" data-id="element-363">
                <h3 className="text-2xl font-heading font-bold text-corporate-dark mb-4 tracking-tight" data-id="element-364">
                  {t('home.dist.partner.title')}
                </h3>
                <p className="text-gray-600 text-base font-light leading-relaxed max-w-sm mx-auto" data-id="element-365">
                  {t('home.dist.partner.desc')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CATALOG PREVIEW SECTION */}
      <section className="py-32 bg-neutral-grey" data-id="element-366">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-sm shadow-2xl overflow-hidden" data-id="element-368">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-369">
              <div className="h-[500px] lg:h-auto relative overflow-hidden group" data-id="element-370">
                <img src="https://cdn.magicpatterns.com/uploads/8KzJfoRaQYshBKXbfrjzn2/Imagen_4_—_Catalogo_de_materiales.png" alt="Catálogo de materiales" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105" data-id="element-371" />
              </div>
              <div className="p-16 md:p-24 flex flex-col justify-center" data-id="element-372">
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-8 tracking-tight" data-id="element-373">
                  {t('home.catalog.title')}
                </h2>
                <p className="text-gray-600 text-xl mb-12 font-light leading-relaxed" data-id="element-374">
                  {t('home.catalog.desc')}
                </p>
                <div data-id="element-375">
                  <Link to="/catalogo" className="inline-flex items-center justify-center px-10 py-5 text-base font-medium text-corporate-dark border-2 border-corporate-dark hover:bg-corporate-dark hover:text-white transition-all duration-500 ease-premium rounded-md hover:scale-[1.02]" data-id="element-376">
                    {t('home.catalog.cta')}
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DIFFERENTIATION SECTION */}
      <section className="py-28 lg:py-36 bg-white border-t border-gray-100" data-id="element-377">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-378">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-379">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-corporate-dark mb-6 tracking-tight" data-id="element-380">
              {t('home.why.title')}
            </h2>
            <div className="w-16 h-0.5 bg-accent-beige mx-auto" data-id="element-381"></div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-382">
            {[{
            icon: Award,
            title: t('home.why.q1.title'),
            desc: t('home.why.q1.desc')
          }, {
            icon: Lightbulb,
            title: t('home.why.q2.title'),
            desc: t('home.why.q2.desc')
          }, {
            icon: Globe,
            title: t('home.why.q3.title'),
            desc: t('home.why.q3.desc')
          }, {
            icon: Briefcase,
            title: t('home.why.q4.title'),
            desc: t('home.why.q4.desc')
          }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="text-center p-8 rounded-sm hover:shadow-lg hover:-translate-y-2 transition-all duration-500 ease-premium bg-white" data-id="element-383">
                <div className="mx-auto w-20 h-20 bg-neutral-grey rounded-full flex items-center justify-center mb-8 text-corporate-blue" data-id="element-384">
                  <item.icon className="h-10 w-10" data-id="element-385" />
                </div>
                <h3 className="text-xl font-heading font-bold text-corporate-dark mb-4" data-id="element-386">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-base font-light leading-relaxed" data-id="element-387">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>
    </div>;
}